import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useScroll } from '../hooks/useScroll';
import useUnreadNotifications from '../hooks/useUnreadNotifications';
import './Navbar.css';

function Navbar() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { isScrolled } = useScroll();
  const unreadCount = useUnreadNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const closeMenu = () => setIsOpen(false);
  const closeUserMenu = () => setShowUserMenu(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('ログアウトに失敗しました', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu-wrapper')) {
        closeUserMenu();
      }
    };

    if (!showUserMenu) return undefined;
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUserMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.menu-icon') && !event.target.closest('.nav-menu')) {
        closeMenu();
      }
    };

    if (!isOpen) return undefined;
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMenu}>
          <picture>
            <source
              srcSet={`${process.env.PUBLIC_URL}/images/log_back_dark.png`}
              media="(prefers-color-scheme: dark)"
            />
            <img
              src={`${process.env.PUBLIC_URL}/images/log_back.png`}
              alt="ヤマッチ - Hike & Meet"
              className="logo-img"
            />
          </picture>
        </Link>

        <div className="menu-icon" onClick={() => setIsOpen((prev) => !prev)}>
          <div className={`hamburger ${isOpen ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>

        <ul className={`nav-menu ${isOpen ? 'active' : ''}`}>
          <li className="nav-item">
            <NavLink
              to="/recruitments"
              className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <span className="nav-text">募集を探す</span>
            </NavLink>
          </li>

          {currentUser && (
            <li className="nav-item">
              <NavLink
                to="/create-recruitment"
                className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
                onClick={closeMenu}
              >
                <span className="nav-text">募集を作る</span>
              </NavLink>
            </li>
          )}

          {currentUser && (
            <li className="nav-item">
              <NavLink
                to="/history"
                className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
                onClick={closeMenu}
              >
                <span className="nav-text">応募履歴</span>
              </NavLink>
            </li>
          )}

          {currentUser && (
            <li className="nav-item">
              <NavLink
                to="/my-recruitments"
                className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
                onClick={closeMenu}
              >
                <span className="nav-text">募集履歴</span>
              </NavLink>
            </li>
          )}

          {currentUser && (
            <li className="nav-item">
              <NavLink
                to="/chats"
                className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
                onClick={closeMenu}
              >
                <span className="nav-text">チャット</span>
              </NavLink>
            </li>
          )}

          {currentUser && (
            <li className="nav-item">
              <NavLink
                to="/notifications"
                className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
                onClick={closeMenu}
              >
                <span className="nav-text">
                  通知{unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
                </span>
              </NavLink>
            </li>
          )}

          <li className="nav-item">
            <NavLink
              to="/mountain-log"
              className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <span className="nav-text">登山記録</span>
            </NavLink>
          </li>

          <li className="nav-item">
            <NavLink
              to="/contact"
              className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <span className="nav-text">お問い合わせ</span>
            </NavLink>
          </li>

          {currentUser ? (
            <>
              <li className="nav-item">
                <NavLink
                  to="/achievements"
                  className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <span className="nav-text">実績一覧</span>
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink
                  to="/profile/edit"
                  className={({ isActive }) => `nav-links ${isActive ? 'active' : ''}`}
                  onClick={closeMenu}
                >
                  <span className="nav-text">プロフィール</span>
                </NavLink>
              </li>
            </>
          ) : (
            <>
              <li className="nav-item">
                <Link to="/login" className="nav-links" onClick={closeMenu}>
                  <span className="nav-text">ログイン</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/signup" className="nav-links signup-btn" onClick={closeMenu}>
                  <span className="nav-text">新規登録</span>
                </Link>
              </li>
            </>
          )}
        </ul>

        {currentUser && (
          <div className="user-info desktop-only">
            <div className="user-menu-wrapper">
              <div className="user-info-content" onClick={() => setShowUserMenu((prev) => !prev)}>
                <div className="user-avatar">
                  {currentUser.photoURL ? (
                    <img src={currentUser.photoURL} alt="ユーザーアイコン" />
                  ) : (
                    <div className="default-avatar">
                      {currentUser.displayName?.charAt(0) || 'U'}
                    </div>
                  )}
                </div>
                <span className="user-name">{currentUser.displayName || 'ユーザー'}</span>
                <span className="dropdown-arrow">▼</span>
              </div>

              <div className={`user-dropdown ${showUserMenu ? 'active' : ''}`}>
                <div className="dropdown-item" onClick={() => { navigate('/profile/edit'); closeUserMenu(); }}>
                  <span>プロフィール編集</span>
                </div>
                <div className="dropdown-item" onClick={() => { navigate('/history'); closeUserMenu(); }}>
                  <span>応募履歴</span>
                </div>
                <div className="dropdown-item" onClick={() => { navigate('/my-recruitments'); closeUserMenu(); }}>
                  <span>募集履歴</span>
                </div>
                <div className="dropdown-item" onClick={() => { navigate('/chats'); closeUserMenu(); }}>
                  <span>チャット一覧</span>
                </div>
                <div className="dropdown-item" onClick={() => { navigate('/notifications'); closeUserMenu(); }}>
                  <span>
                    通知{unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
                  </span>
                </div>
                <div className="dropdown-divider"></div>
                <div className="dropdown-item logout-item" onClick={() => { handleLogout(); closeUserMenu(); }}>
                  <span>ログアウト</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isOpen && <div className="nav-overlay" onClick={closeMenu} />}
      {showUserMenu && <div className="user-menu-overlay" onClick={closeUserMenu} />}
    </nav>
  );
}

export default Navbar;
